import { AppImage } from '@/components/ui/app-image';
import { Text } from '@/components/ui/text';
import type { Note } from '@/types/note';
import { formatRelativeTime } from '@/utils/format-time';
import Ionicons from '@expo/vector-icons/Ionicons';
import { Pressable, View } from 'react-native';

interface NoteCardProps {
  note: Note;
  onEdit: (note: Note) => void;
  onDelete: (note: Note) => void;
}

export function NoteCard({ note, onEdit, onDelete }: NoteCardProps) {
  const photoKey = note.photo ? note.photo.r2Key ?? note.photo.imageUrl : null;
  const photoLabel = note.photo?.altText
    || (note.menuItemName ? `Note photo of ${note.menuItemName}` : 'Note photo');

  return (
    <View className="border border-border rounded-xl p-4 mb-3 bg-surface" style={{ borderCurve: 'continuous' }}>
      <View className="flex-row items-center justify-between mb-2">
        <Text className="text-xs text-muted-foreground/70">
          {formatRelativeTime(note.createdAt)}
        </Text>
      </View>

      <View className="flex-row">
        <View className="flex-1 mr-3">
          <Text className="text-sm text-foreground leading-5" numberOfLines={4}>
            {note.content}
          </Text>
        </View>

        {photoKey && (
          <View
            className="rounded-lg overflow-hidden bg-surface-secondary"
            style={{ width: 56, height: 56, borderCurve: 'continuous' }}
          >
            <AppImage
              r2Key={photoKey}
              preset="thumbnail"
              style={{ width: 56, height: 56 }}
              accessibilityLabel={photoLabel}
            />
          </View>
        )}
      </View>

      <View className="flex-row items-center justify-end mt-3 pt-2 border-t border-border/50">
        <Pressable
          onPress={() => onEdit(note)}
          hitSlop={8}
          className="flex-row items-center mr-4 py-1"
          accessibilityRole="button"
          accessibilityLabel="Edit note"
        >
          <Ionicons name="pencil-outline" size={16} className="text-muted-foreground" />
          <Text className="text-xs text-muted-foreground ml-1">Edit</Text>
        </Pressable>

        <Pressable
          onPress={() => onDelete(note)}
          hitSlop={8}
          className="flex-row items-center py-1"
          accessibilityRole="button"
          accessibilityLabel="Delete note"
        >
          <Ionicons name="trash-outline" size={16} className="text-destructive" />
          <Text className="text-xs text-destructive ml-1">Delete</Text>
        </Pressable>
      </View>
    </View>
  );
}
